import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Menu,
  X,
  Grid3X3,
  Building2,
  GraduationCap,
  Landmark,
  School,
  ChevronDown,
  LayoutGrid,
  BarChart3,
  ScanLine,
  UserCog,
  Utensils,
  Camera,
  Bell,
  BellRing,
  CreditCard,
  CalendarDays,
  Users,
  Activity,
  Sun,
  Package,
  FileText,
  MapPin,
  MessageCircle,
  Monitor,
  Smartphone,
  Tag,
} from "lucide-react";
import Logo from "@/components/Logo";
import {
  ACADEMY_PORTAL_URL,
  COLLAGE_PORTAL_URL,
  DAYCARE_PORTAL_URL,
  PRESCHOOL_PORTAL_URL,
  SCHOOL_PORTAL_URL,
} from "@/lib/config";

const NAV = [
  { name: "Home", to: "/" },
  { name: "Journal", to: "/blog" },
  { name: "FAQ", to: "/faq" },
];

const DAYCARE_FEATURE_LINKS = [
  { name: "Overview", to: "/features", icon: LayoutGrid },
  { name: "Dashboard", to: "/dashboard", icon: BarChart3 },
  { name: "Attendance", to: "/attendance", icon: ScanLine },
  { name: "Kids", to: "/kids", icon: Users },
  { name: "Staff", to: "/staff", icon: UserCog },
  { name: "Food Menu", to: "/food-menu", icon: Utensils },
  { name: "Gallery", to: "/gallery", icon: Camera },
  { name: "Notifications", to: "/notifications", icon: Bell },
  { name: "Payments", to: "/payments", icon: CreditCard },
  { name: "Schedule", to: "/schedule", icon: CalendarDays },
];

const PRODUCT_LINKS = [
  { name: "Daycare Management", to: "/daycare-management", icon: Monitor },
  { name: "Features", to: "/features", icon: LayoutGrid },
  { name: "Parent App", to: "/parent-app", icon: Smartphone },
  { name: "Pricing", to: "/pricing", icon: Tag },
];

const PARENT_FEATURE_LINKS = [
  { name: "Activity Feed", to: "/activity-feed", icon: Activity },
  { name: "Messages", to: "/messages", icon: MessageCircle },
  { name: "Notifications", to: "/parent-notifications", icon: BellRing },
  { name: "Holidays", to: "/holidays", icon: Sun },
  { name: "Lost & Found", to: "/lost-and-found", icon: Package },
  { name: "Reports", to: "/reports", icon: FileText },
  { name: "Find Daycares", to: "/find-daycares", icon: MapPin },
];

const ALL_FEATURE_LINKS = [...DAYCARE_FEATURE_LINKS, ...PARENT_FEATURE_LINKS];

const APP_MENU = [
  {
    name: "Daycare",
    icon: Building2,
    to: DAYCARE_PORTAL_URL,
    enabled: true,
  },
  {
    name: "Pre-School",
    icon: School,
    to: PRESCHOOL_PORTAL_URL,
    enabled: false,
  },
  {
    name: "School",
    icon: School,
    to: SCHOOL_PORTAL_URL,
    enabled: false,
  },
  {
    name: "Academy",
    icon: GraduationCap,
    to: ACADEMY_PORTAL_URL,
    enabled: false,
  },
  {
    name: "Collage",
    icon: Landmark,
    to: COLLAGE_PORTAL_URL,
    enabled: false,
  },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [appMenuOpen, setAppMenuOpen] = useState(false);
  const [productOpen, setProductOpen] = useState(false);
  const [featuresOpen, setFeaturesOpen] = useState(false);
  const [mobileFeaturesOpen, setMobileFeaturesOpen] = useState(false);
  const [mobileProductOpen, setMobileProductOpen] = useState(false);
  const appMenuRef = useRef(null);
  const productMenuRef = useRef(null);
  const featuresMenuRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  const isFeaturesActive = ALL_FEATURE_LINKS.some((f) => location.pathname === f.to);
  const isProductActive = PRODUCT_LINKS.some((p) => location.pathname === p.to);

  useEffect(() => {
    setFeaturesOpen(false);
    setProductOpen(false);
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const onClickOutside = (event) => {
      if (appMenuRef.current && !appMenuRef.current.contains(event.target)) {
        setAppMenuOpen(false);
      }
      if (productMenuRef.current && !productMenuRef.current.contains(event.target)) {
        setProductOpen(false);
      }
      if (featuresMenuRef.current && !featuresMenuRef.current.contains(event.target)) {
        setFeaturesOpen(false);
      }
    };
    const onEscape = (event) => {
      if (event.key === "Escape") {
        setAppMenuOpen(false);
        setProductOpen(false);
        setFeaturesOpen(false);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    document.addEventListener("keydown", onEscape);
    return () => {
      document.removeEventListener("mousedown", onClickOutside);
      document.removeEventListener("keydown", onEscape);
    };
  }, []);

  const FeatureLinkItem = ({ item }) => {
    const Icon = item.icon;
    return (
      <Link
        to={item.to}
        role="menuitem"
        onClick={() => setFeaturesOpen(false)}
        className={`flex items-center gap-3 px-3 py-2 rounded-xl text-sm font-medium transition-colors ${
          location.pathname === item.to
            ? "bg-[var(--cn-sage-bg)] text-ink"
            : "text-ink-soft hover:bg-[var(--cn-paper-2)] hover:text-ink"
        }`}
      >
        <Icon className="w-4 h-4 text-[var(--cn-sage-dark)] shrink-0" />
        {item.name}
      </Link>
    );
  };

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-[#2f3b3f]/95 backdrop-blur-xl border-b border-[#435055]"
          : "bg-[#334146]/90 backdrop-blur-md border-b border-[#46545a]"
      }`}
    >
      <div className="container-ss flex items-center justify-between h-20">
        <Link to="/" data-testid="navbar-logo" className="group">
          <Logo tone="inverse" />
        </Link>

        <nav className="hidden lg:flex items-center gap-2">
          <NavLink
            to="/"
            end
            data-testid="nav-home"
            className={({ isActive }) =>
              `px-4 py-2 text-sm font-medium transition-colors ${
                isActive
                  ? "text-[var(--cn-cream)] nav-active"
                  : "text-[#d2d9cf] hover:text-[var(--cn-cream)]"
              }`
            }
          >
            Home
          </NavLink>

          {/* Product dropdown */}
          <div className="relative" ref={productMenuRef}>
            <button
              type="button"
              data-testid="nav-product-btn"
              onClick={() => setProductOpen((prev) => !prev)}
              className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium transition-colors ${
                isProductActive
                  ? "text-[var(--cn-cream)] nav-active"
                  : "text-[#d2d9cf] hover:text-[var(--cn-cream)]"
              }`}
              aria-haspopup="menu"
              aria-expanded={productOpen}
            >
              Product
              <ChevronDown
                className={`w-3.5 h-3.5 transition-transform duration-200 ${
                  productOpen ? "rotate-180" : ""
                }`}
              />
            </button>

            <AnimatePresence>
              {productOpen && (
                <motion.div
                  initial={{ opacity: 0, y: 8, scale: 0.98 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 8, scale: 0.98 }}
                  transition={{ duration: 0.18 }}
                  className="absolute left-0 top-12 w-56 overflow-hidden bg-[var(--cn-paper)] border hairline rounded-2xl shadow-[0_20px_40px_rgba(45,55,66,0.14)]"
                  role="menu"
                >
                  <div className="border-b border-[var(--cn-sage-dark)] bg-[var(--cn-sage-dark)] px-4 py-2.5">
                    <p className="text-xs font-semibold tracking-[0.12em] uppercase text-[var(--cn-cream)]">
                      Product
                    </p>
                  </div>
                  <div className="p-2">
                    {PRODUCT_LINKS.map((item) => {
                      const Icon = item.icon;
                      return (
                        <Link
                          key={item.to}
                          to={item.to}
                          role="menuitem"
                          onClick={() => setProductOpen(false)}
                          className={`flex items-center gap-3 px-3 py-2 rounded-xl text-sm font-medium transition-colors ${
                            location.pathname === item.to
                              ? "bg-[var(--cn-sage-bg)] text-ink"
                              : "text-ink-soft hover:bg-[var(--cn-paper-2)] hover:text-ink"
                          }`}
                        >
                          <Icon className="w-4 h-4 text-[var(--cn-sage-dark)] shrink-0" />
                          {item.name}
                        </Link>
                      );
                    })}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Features mega-menu */}
          <div className="relative" ref={featuresMenuRef}>
            <button
              type="button"
              data-testid="nav-features-btn"
              onClick={() => setFeaturesOpen((prev) => !prev)}
              className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium transition-colors ${
                isFeaturesActive
                  ? "text-[var(--cn-cream)] nav-active"
                  : "text-[#d2d9cf] hover:text-[var(--cn-cream)]"
              }`}
              aria-haspopup="menu"
              aria-expanded={featuresOpen}
            >
              Features
              <ChevronDown
                className={`w-3.5 h-3.5 transition-transform duration-200 ${
                  featuresOpen ? "rotate-180" : ""
                }`}
              />
            </button>

            <AnimatePresence>
              {featuresOpen && (
                <motion.div
                  initial={{ opacity: 0, y: 8, scale: 0.98 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 8, scale: 0.98 }}
                  transition={{ duration: 0.18 }}
                  className="absolute left-0 top-12 w-64 overflow-hidden bg-[var(--cn-paper)] border hairline rounded-2xl shadow-[0_20px_40px_rgba(45,55,66,0.14)] flex flex-col max-h-[80vh]"
                  role="menu"
                >
                  <div className="border-b border-[var(--cn-sage-dark)] bg-[var(--cn-sage-dark)] px-4 py-2.5 flex-shrink-0">
                    <p className="text-xs font-semibold tracking-[0.12em] uppercase text-[var(--cn-cream)]">
                      Features
                    </p>
                  </div>
                  <div className="overflow-y-auto p-2">
                    <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-[var(--cn-sage-dark)] px-2 pt-1.5 pb-0.5">
                      Daycare
                    </p>
                    {DAYCARE_FEATURE_LINKS.map((item) => (
                      <FeatureLinkItem key={item.to} item={item} />
                    ))}
                    <div className="my-2 border-t hairline" />
                    <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-[var(--cn-sage-dark)] px-2 pt-0.5 pb-0.5">
                      Parent App
                    </p>
                    {PARENT_FEATURE_LINKS.map((item) => (
                      <FeatureLinkItem key={item.to} item={item} />
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {NAV.filter((item) => item.to !== "/").map((item) => (
            <NavLink
              key={item.name}
              to={item.to}
              data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `px-4 py-2 text-sm font-medium transition-colors ${
                  isActive
                    ? "text-[var(--cn-cream)] nav-active"
                    : "text-[#d2d9cf] hover:text-[var(--cn-cream)]"
                }`
              }
            >
              {item.name}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-2">
          <div className="relative" ref={appMenuRef}>
            <button
              type="button"
              data-testid="navbar-app-menu-btn"
              onClick={() => setAppMenuOpen((prev) => !prev)}
              className="inline-flex items-center justify-center p-2.5 text-sm font-medium text-[#d2d9cf] hover:text-[var(--cn-cream)] bg-[#3b494e] border border-[#4a5960] rounded-xl transition-colors"
              aria-haspopup="menu"
              aria-expanded={appMenuOpen}
              aria-label="Open app menu"
            >
              <Grid3X3 className="w-4 h-4" />
            </button>

            <AnimatePresence>
              {appMenuOpen && (
                <motion.div
                  initial={{ opacity: 0, y: 8, scale: 0.98 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 8, scale: 0.98 }}
                  transition={{ duration: 0.18 }}
                  className="absolute right-0 top-12 w-80 overflow-hidden bg-[var(--cn-paper)] border hairline rounded-2xl shadow-[0_20px_40px_rgba(45,55,66,0.14)]"
                >
                  <div className="w-full border-b border-[var(--cn-sage-dark)] bg-[var(--cn-sage-dark)] px-4 py-3">
                    <p className="text-xs font-semibold tracking-[0.12em] uppercase text-[var(--cn-cream)]">
                      Your portals
                    </p>
                  </div>
                  <div className="mt-3 grid grid-cols-2 gap-2 px-4 pb-4">
                    {APP_MENU.map((item) => {
                      const Icon = item.icon;
                      return (
                        <button
                          key={item.name}
                          type="button"
                          onClick={() => {
                            if (!item.enabled) return;
                            window.open(item.to, "_blank", "noopener,noreferrer");
                            setAppMenuOpen(false);
                          }}
                          disabled={!item.enabled}
                          aria-disabled={!item.enabled}
                          className={`flex items-center gap-2 rounded-xl border hairline px-3 py-3 text-sm font-medium transition-colors ${
                            item.enabled
                              ? "bg-[var(--cn-cream)] text-ink-soft hover:bg-[var(--cn-cream-2)] hover:text-ink"
                              : "bg-[var(--cn-paper-2)] text-[var(--cn-muted)] cursor-not-allowed opacity-70"
                          }`}
                        >
                          <Icon
                            className={`w-4 h-4 ${
                              item.enabled ? "text-[var(--cn-sage-dark)]" : "text-[var(--cn-muted)]"
                            }`}
                          />
                          {item.name}
                        </button>
                      );
                    })}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          <button
            data-testid="navbar-book-demo-btn"
            onClick={() => navigate("/book-demo")}
            className="btn-primary !py-2.5 !px-5 text-sm gap-2"
          >
            Book a demo
            <span className="w-1.5 h-1.5 rounded-full bg-[var(--cn-terra)] pulse-dot" />
          </button>
        </div>

        <button
          data-testid="navbar-mobile-toggle"
          className="lg:hidden p-2 rounded-xl bg-[#3b494e] border border-[#4a5960] text-[#d2d9cf]"
          onClick={() => setMobileOpen((s) => !s)}
        >
          {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-[var(--cn-cream)] backdrop-blur-xl border-t hairline overflow-hidden"
          >
            <div className="container-ss py-4 flex flex-col gap-1">
              <NavLink
                to="/"
                end
                onClick={() => setMobileOpen(false)}
                data-testid="mobile-nav-home"
                className="px-4 py-3 rounded-xl font-medium text-ink-soft hover:bg-[var(--cn-paper)]"
              >
                Home
              </NavLink>

              {/* Mobile Product accordion */}
              <div>
                <button
                  type="button"
                  data-testid="mobile-nav-product-toggle"
                  onClick={() => setMobileProductOpen((s) => !s)}
                  className={`w-full flex items-center justify-between px-4 py-3 rounded-xl font-medium transition-colors ${
                    isProductActive || mobileProductOpen
                      ? "bg-[var(--cn-paper)] text-ink"
                      : "text-ink-soft hover:bg-[var(--cn-paper)]"
                  }`}
                >
                  Product
                  <ChevronDown
                    className={`w-4 h-4 transition-transform duration-200 ${
                      mobileProductOpen ? "rotate-180" : ""
                    }`}
                  />
                </button>
                <AnimatePresence>
                  {mobileProductOpen && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      className="overflow-hidden pl-3"
                    >
                      <div className="py-1">
                        {PRODUCT_LINKS.map((item) => {
                          const Icon = item.icon;
                          return (
                            <Link
                              key={item.to}
                              to={item.to}
                              onClick={() => setMobileOpen(false)}
                              data-testid={`mobile-product-${item.name.toLowerCase().replace(/\s+/g, "-")}`}
                              className={`flex items-center gap-3 px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                                location.pathname === item.to
                                  ? "bg-[var(--cn-sage-bg)] text-ink"
                                  : "text-ink-soft hover:bg-[var(--cn-paper)] hover:text-ink"
                              }`}
                            >
                              <Icon className="w-4 h-4 text-[var(--cn-sage-dark)] shrink-0" />
                              {item.name}
                            </Link>
                          );
                        })}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Mobile Features accordion */}
              <div>
                <button
                  type="button"
                  data-testid="mobile-nav-features-toggle"
                  onClick={() => setMobileFeaturesOpen((s) => !s)}
                  className={`w-full flex items-center justify-between px-4 py-3 rounded-xl font-medium transition-colors ${
                    isFeaturesActive || mobileFeaturesOpen
                      ? "bg-[var(--cn-paper)] text-ink"
                      : "text-ink-soft hover:bg-[var(--cn-paper)]"
                  }`}
                >
                  Features
                  <ChevronDown
                    className={`w-4 h-4 transition-transform duration-200 ${
                      mobileFeaturesOpen ? "rotate-180" : ""
                    }`}
                  />
                </button>
                <AnimatePresence>
                  {mobileFeaturesOpen && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      className="overflow-hidden pl-3"
                    >
                      <div className="py-1">
                        <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-[var(--cn-sage-dark)] px-4 pt-2 pb-1">
                          Daycare
                        </p>
                        {DAYCARE_FEATURE_LINKS.map((item) => {
                          const Icon = item.icon;
                          return (
                            <Link
                              key={item.to}
                              to={item.to}
                              onClick={() => setMobileOpen(false)}
                              data-testid={`mobile-feature-${item.name.toLowerCase().replace(/\s+/g, "-")}`}
                              className={`flex items-center gap-3 px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                                location.pathname === item.to
                                  ? "bg-[var(--cn-sage-bg)] text-ink"
                                  : "text-ink-soft hover:bg-[var(--cn-paper)] hover:text-ink"
                              }`}
                            >
                              <Icon className="w-4 h-4 text-[var(--cn-sage-dark)] shrink-0" />
                              {item.name}
                            </Link>
                          );
                        })}
                        <div className="mx-4 my-2 border-t hairline" />
                        <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-[var(--cn-sage-dark)] px-4 pb-1">
                          Parent App
                        </p>
                        {PARENT_FEATURE_LINKS.map((item) => {
                          const Icon = item.icon;
                          return (
                            <Link
                              key={item.to}
                              to={item.to}
                              onClick={() => setMobileOpen(false)}
                              data-testid={`mobile-feature-${item.name.toLowerCase().replace(/\s+/g, "-")}`}
                              className={`flex items-center gap-3 px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                                location.pathname === item.to
                                  ? "bg-[var(--cn-sage-bg)] text-ink"
                                  : "text-ink-soft hover:bg-[var(--cn-paper)] hover:text-ink"
                              }`}
                            >
                              <Icon className="w-4 h-4 text-[var(--cn-sage-dark)] shrink-0" />
                              {item.name}
                            </Link>
                          );
                        })}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {NAV.filter((item) => item.to !== "/").map((item) => (
                <NavLink
                  key={item.name}
                  to={item.to}
                  onClick={() => setMobileOpen(false)}
                  data-testid={`mobile-nav-${item.name.toLowerCase().replace(/\s+/g, "-")}`}
                  className="px-4 py-3 rounded-xl font-medium text-ink-soft hover:bg-[var(--cn-paper)]"
                >
                  {item.name}
                </NavLink>
              ))}

              <div className="grid grid-cols-2 gap-2 pt-2">
                {APP_MENU.map((item) => (
                  <Link
                    key={item.name}
                    to={item.to}
                    onClick={(event) => {
                      event.preventDefault();
                      if (!item.enabled) return;
                      window.open(item.to, "_blank", "noopener,noreferrer");
                      setMobileOpen(false);
                    }}
                    data-testid={`mobile-menu-${item.name.toLowerCase()}`}
                    aria-disabled={!item.enabled}
                    tabIndex={item.enabled ? 0 : -1}
                    className={`rounded-xl border hairline px-3 py-2 text-center text-sm font-medium ${
                      item.enabled
                        ? "bg-[var(--cn-paper)] text-ink-soft hover:text-ink"
                        : "bg-[var(--cn-paper-2)] text-[var(--cn-muted)] cursor-not-allowed opacity-70 pointer-events-none"
                    }`}
                  >
                    {item.name}
                  </Link>
                ))}
              </div>
              <div className="flex gap-2 pt-2">
                <Link
                  to="/book-demo"
                  onClick={() => setMobileOpen(false)}
                  data-testid="mobile-book-demo-btn"
                  className="flex-1 btn-primary justify-center"
                >
                  Book a demo
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
